import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { getAdmins, saveAdmins } from "../../api/admin";
import { getUser, isSuperAdmin, ClientPrincipal } from "../../auth/user";
import { PERMISSION_KEYS, PERMISSION_LABELS, Permission } from "../../auth/permissions";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const GRANULAR_PERMISSIONS = PERMISSION_KEYS.filter((k) => k !== "super") as Exclude<Permission, "super">[];

// Mirrors the API's default baseline for a regular admin with no explicit record.
const DEFAULT_ADMIN_PERMISSIONS: Permission[] = ["submissions:view", "submissions:edit", "formEdit:view"];

interface PersonSuggestion {
  id: string;
  displayName: string;
  email: string;
  jobTitle?: string;
  department?: string;
}

export default function AdminsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<ClientPrincipal | null>(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [admins, setAdmins] = useState<string[]>([]);
  const [superAdmins, setSuperAdmins] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<Record<string, string[]>>({});
  const [expandedPerms, setExpandedPerms] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [makeSuper, setMakeSuper] = useState(false);
  const [suggestions, setSuggestions] = useState<PersonSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const suggestSeq = useRef(0);
  const inputWrapRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    getUser().then((u) => {
      setUser(u);
      setUserLoaded(true);
    });
  }, []);

  useEffect(() => {
    getAdmins()
      .then((d) => {
        setAdmins(d.admins);
        setSuperAdmins(d.superAdmins);
        setPermissions(d.permissions ?? {});
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const q = newEmail.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    const seq = ++suggestSeq.current;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`/api/people-search?q=${encodeURIComponent(q)}`);
        if (!r.ok) return;
        const j = (await r.json()) as {
          results: Array<{ id: string; displayName: string; jobTitle?: string; department?: string; mail?: string; userPrincipalName?: string }>;
        };
        if (seq !== suggestSeq.current) return;
        const mapped: PersonSuggestion[] = (j.results ?? [])
          .map((p) => ({
            id: p.id,
            displayName: p.displayName,
            email: (p.mail || p.userPrincipalName || "").toLowerCase(),
            jobTitle: p.jobTitle,
            department: p.department
          }))
          .filter((p) => p.email);
        setSuggestions(mapped);
        setHighlight(0);
      } catch {
        /* ignore */
      }
    }, 200);
    return () => clearTimeout(t);
  }, [newEmail]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!inputWrapRef.current) return;
      if (!inputWrapRef.current.contains(e.target as Node)) setShowSuggestions(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const currentEmail = (user?.userDetails ?? "").toLowerCase();
  const allEmails = useMemo(() => {
    const set = new Set<string>([...admins, ...superAdmins]);
    return Array.from(set).sort();
  }, [admins, superAdmins]);

  if (userLoaded && !isSuperAdmin(user)) {
    return <div className="banner-error">Super admin required.</div>;
  }

  const addEmail = (override?: string) => {
    setError(null);
    const e = (override ?? newEmail).trim().toLowerCase();
    if (!e) return;
    if (!EMAIL_RE.test(e)) {
      setError("Enter a valid email address");
      return;
    }
    if (!admins.includes(e)) setAdmins((arr) => [...arr, e]);
    if (makeSuper && !superAdmins.includes(e)) setSuperAdmins((arr) => [...arr, e]);
    setNewEmail("");
    setMakeSuper(false);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const pickSuggestion = (p: PersonSuggestion) => addEmail(p.email);

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => (h + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        pickSuggestion(suggestions[highlight]);
        return;
      }
      if (e.key === "Escape") {
        setShowSuggestions(false);
        return;
      }
    }
    if (e.key === "Enter") {
      e.preventDefault();
      addEmail();
    }
  };

  const removeEmail = (email: string) => {
    setError(null);
    if (email === currentEmail) {
      if (!confirm("Remove yourself from admins? You will lose access immediately after saving.")) return;
    }
    setAdmins((arr) => arr.filter((x) => x !== email));
    setSuperAdmins((arr) => arr.filter((x) => x !== email));
  };

  const toggleSuper = (email: string, next: boolean) => {
    setError(null);
    if (next) {
      if (!superAdmins.includes(email)) setSuperAdmins((arr) => [...arr, email]);
      if (!admins.includes(email)) setAdmins((arr) => [...arr, email]);
    } else {
      if (email === currentEmail) {
        if (!confirm("Remove your own super-admin rights? You won't be able to manage admins after saving.")) return;
      }
      setSuperAdmins((arr) => arr.filter((x) => x !== email));
    }
  };

  const effectivePerms = (email: string): string[] => {
    const stored = permissions[email];
    if (stored && stored.length) return stored;
    return DEFAULT_ADMIN_PERMISSIONS;
  };

  const togglePerm = (email: string, perm: Permission, checked: boolean) => {
    setPermissions((prev) => {
      const base = prev[email] && prev[email].length ? prev[email] : DEFAULT_ADMIN_PERMISSIONS;
      const set = new Set(base);
      if (checked) set.add(perm);
      else set.delete(perm);
      return { ...prev, [email]: Array.from(set) };
    });
  };

  const onSave = async () => {
    setError(null);
    setSaving(true);
    try {
      // Only persist permission records for regular (non-super) admins.
      const cleanedPerms: Record<string, string[]> = {};
      for (const [email, perms] of Object.entries(permissions)) {
        if (admins.includes(email) && !superAdmins.includes(email)) cleanedPerms[email] = perms;
      }
      const saved = await saveAdmins(admins, superAdmins, cleanedPerms);
      setAdmins(saved.admins);
      setSuperAdmins(saved.superAdmins);
      setPermissions(saved.permissions ?? {});
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="admin-page-head">
        <h1>Admins</h1>
        <button type="button" className="btn-secondary" onClick={() => navigate("/admin")}>Done</button>
      </div>
      <p className="hint">
        Admins can view and edit submissions. Super admins can additionally manage admins, progress stages, and the form.
        Changes take effect the next time each user signs in (or within ~30 seconds for API calls).
      </p>
      {error && <div className="banner-error">{error}</div>}
      <div className="card">
        {loading ? (
          <div className="hint">Loading…</div>
        ) : (
          <>
            <table className="admin-table compact" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th style={{ width: "8rem" }}>Super admin</th>
                  <th style={{ width: "10rem" }}>Permissions</th>
                  <th style={{ width: "6rem" }}></th>
                </tr>
              </thead>
              <tbody>
                {allEmails.length === 0 && (
                  <tr><td colSpan={4} className="hint">No admins configured yet.</td></tr>
                )}
                {allEmails.map((email) => {
                  const isSuper = superAdmins.includes(email);
                  const expanded = expandedPerms === email;
                  const perms = effectivePerms(email);
                  return (
                    <Fragment key={email}>
                      <tr>
                        <td>
                          {email}
                          {email === currentEmail && <span className="hint"> (you)</span>}
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            checked={isSuper}
                            onChange={(e) => toggleSuper(email, e.target.checked)}
                          />
                        </td>
                        <td>
                          {isSuper ? (
                            <span className="hint">All (super admin)</span>
                          ) : (
                            <button
                              type="button"
                              className="btn-secondary btn-sm"
                              onClick={() => setExpandedPerms(expanded ? null : email)}
                            >
                              {expanded ? "Hide" : "Edit"} ({perms.length})
                            </button>
                          )}
                        </td>
                        <td>
                          <button type="button" className="btn-secondary" onClick={() => removeEmail(email)}>Remove</button>
                        </td>
                      </tr>
                      {expanded && !isSuper && (
                        <tr className="perm-row">
                          <td colSpan={4}>
                            <div className="perm-grid">
                              {GRANULAR_PERMISSIONS.map((perm) => (
                                <label key={perm} className="perm-check">
                                  <input
                                    type="checkbox"
                                    checked={perms.includes(perm)}
                                    onChange={(e) => togglePerm(email, perm, e.target.checked)}
                                  />
                                  {PERMISSION_LABELS[perm]}
                                </label>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>

            <div className="actions" style={{ marginTop: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
              <div ref={inputWrapRef} style={{ position: "relative", flex: "1 1 18rem", minWidth: "12rem" }}>
                <input
                  type="text"
                  placeholder="Search by name or email"
                  value={newEmail}
                  onChange={(e) => { setNewEmail(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  onKeyDown={onInputKeyDown}
                  autoComplete="off"
                  style={{ width: "100%" }}
                />
                {showSuggestions && suggestions.length > 0 && (
                  <ul
                    role="listbox"
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 20,
                      margin: 0,
                      padding: 0,
                      listStyle: "none",
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: 4,
                      boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
                      maxHeight: "16rem",
                      overflowY: "auto"
                    }}
                  >
                    {suggestions.map((p, i) => {
                      const already = admins.includes(p.email);
                      return (
                        <li
                          key={p.id}
                          role="option"
                          aria-selected={i === highlight}
                          onMouseEnter={() => setHighlight(i)}
                          onMouseDown={(e) => { e.preventDefault(); if (!already) pickSuggestion(p); }}
                          style={{
                            padding: "0.5rem 0.75rem",
                            cursor: already ? "not-allowed" : "pointer",
                            background: i === highlight ? "#f0f4ff" : "transparent",
                            opacity: already ? 0.55 : 1,
                            borderBottom: "1px solid #f0f0f0"
                          }}
                        >
                          <div style={{ fontWeight: 500 }}>
                            {p.displayName}
                            {already && <span className="hint" style={{ marginLeft: "0.5rem" }}>· already added</span>}
                          </div>
                          <div className="hint" style={{ fontSize: "0.85em" }}>
                            {p.email}
                            {p.jobTitle ? ` · ${p.jobTitle}` : ""}
                            {p.department ? ` · ${p.department}` : ""}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <label className="hint" style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                <input type="checkbox" checked={makeSuper} onChange={(e) => setMakeSuper(e.target.checked)} />
                Super admin
              </label>
              <button type="button" className="btn-secondary" onClick={() => addEmail()}>+ Add</button>
            </div>

            <div className="actions" style={{ marginTop: "1rem" }}>
              <button type="button" className="btn-save" onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
              {savedAt && <span className="hint">Saved at {savedAt}</span>}
            </div>
          </>
        )}
      </div>
    </>
  );
}
